import React, { useEffect, useState } from 'react';
import { Mail, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { emailVerificationApi } from '../lib/api';
import { claimPendingInvite } from '../lib/pendingInvite';

type VerificationState = 'checking' | 'verified' | 'unverified';

const EmailVerificationBanner: React.FC = () => {
  const { user } = useAuth();
  const [state, setState] = useState<VerificationState>('checking');
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'info' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (!user) return;
    let active = true;
    (async () => {
      try {
        const data = await emailVerificationApi.status();
        if (!active) return;
        setState(data?.verified === true ? 'verified' : 'unverified');
      } catch {
        if (active) setState('unverified');
      }
    })();
    return () => { active = false; };
  }, [user]);

  if (!user || state === 'checking' || state === 'verified') return null;

  const handleSendCode = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const data = await emailVerificationApi.sendCode();
      setCodeSent(true);
      setMessage({ tone: 'info', text: `We sent a 6-digit code to ${data?.email ?? 'your email address'}.` });
    } catch (err) {
      setMessage({ tone: 'error', text: err instanceof Error ? err.message : 'Something went wrong. Please try again.' });
    } finally {
      setBusy(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setMessage(null);
    try {
      const data = await emailVerificationApi.verifyCode(code.trim());
      if (data?.verified === true) {
        setState('verified');
        claimPendingInvite().catch(() => undefined);
        return;
      }
      setMessage({ tone: 'error', text: 'Something went wrong. Please try again.' });
    } catch (err) {
      setMessage({ tone: 'error', text: err instanceof Error ? err.message : 'Something went wrong. Please try again.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-amber-50 border-b border-amber-200 px-4 sm:px-6 lg:px-8 py-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <Mail className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-slate-900">Verify your email address</p>
            <p className="text-sm text-slate-700">
              Verification is required before you can send emails or request affiliate payouts.
            </p>
            {message && (
              <p className={`text-sm mt-1 ${message.tone === 'error' ? 'text-red-700' : 'text-slate-700'}`}>{message.text}</p>
            )}
          </div>
        </div>

        {!codeSent ? (
          <button
            type="button"
            onClick={handleSendCode}
            disabled={busy}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60 transition-colors"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            Send verification code
          </button>
        ) : (
          <form onSubmit={handleVerify} className="flex flex-wrap items-center gap-2">
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              placeholder="6-digit code"
              className="w-36 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
            />
            <button
              type="submit"
              disabled={busy || code.length !== 6}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60 transition-colors"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              Verify
            </button>
            <button
              type="button"
              onClick={handleSendCode}
              disabled={busy}
              className="rounded-lg px-3 py-2 text-sm font-medium text-slate-700 hover:bg-amber-100 disabled:opacity-60 transition-colors"
            >
              Resend code
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default EmailVerificationBanner;
